import { useLibraryStore } from '@/stores/library-store'
import { useAuthStore } from '@/stores/auth-store'
import { FolderTreeItem } from '@/components/FolderTreeItem'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { ResizeHandle } from '@/components/ui/ResizeHandle'
import { Plus } from 'lucide-react'

interface FolderTreeSidebarProps {
  width: number
  /** Resize handle props from useResizablePanel. */
  handleProps: {
    onMouseDown: (e: React.MouseEvent) => void
    onDoubleClick: () => void
  }
  /** Whether the user is actively dragging the resize handle. */
  isResizing?: boolean
  /** Called when the user creates a subfolder via the per-folder hover '+'. */
  onNewSubfolder?: (parentId: string) => void
  /** Called when the user clicks the section-header '+' (creates a root folder). */
  onNewRootFolder?: () => void
}

export function FolderTreeSidebar({
  width,
  handleProps,
  isResizing,
  onNewSubfolder,
  onNewRootFolder,
}: FolderTreeSidebarProps) {
  const { getRootFolders } = useLibraryStore()
  const rootFolders = getRootFolders()
  const isViewer = useAuthStore((s) => s.role === 'viewer')

  return (
    <>
      <div
        className="flex flex-col flex-shrink-0 border-r border-border"
        style={{ width }}
      >
        <ScrollArea className="flex-1">
          <div className="p-2">
            {/* Section Header */}
            <div className="flex items-center justify-between px-2 py-1">
              <span className="text-sm font-medium text-muted-foreground">
                My Folders
              </span>
              {!isViewer && onNewRootFolder && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 rounded-md"
                  onClick={onNewRootFolder}
                  title="New root folder"
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            {/* Folder Tree */}
            <div className="mt-1 space-y-0.5">
              {rootFolders.map((folder) => (
                <FolderTreeItem
                  key={folder.id}
                  folder={folder}
                  level={0}
                  {...(onNewSubfolder && { onNewSubfolder })}
                />
              ))}
            </div>
          </div>
        </ScrollArea>
      </div>

      <ResizeHandle
        onMouseDown={handleProps.onMouseDown}
        onDoubleClick={handleProps.onDoubleClick}
        isResizing={isResizing ?? false}
        aria-label="Resize folder tree"
      />
    </>
  )
}
